import axios from "axios";
import type { ResponseType } from "axios";
import apiClient, {
  PW_SERVER,
  EMISSION_BASE,
  ORGANISATION_BASE,
  SRM_BASE,
  USER_BASE,
  NOTIFICATION_BASE,
  VERIFY_BASE,
  ASSET_BASE,
  MODULE_BASE,
  AUDIT_BASE,
  SUBSCRIPTION_BASE,
  ACCESS_MANAGER_BASE,
  SECURITY_BASE,
} from "@/api/config";
import { handleApiError } from "@/utils/errorHandler";

export {
  PW_SERVER,
  EMISSION_BASE,
  ORGANISATION_BASE,
  SRM_BASE,
  USER_BASE,
  NOTIFICATION_BASE,
  VERIFY_BASE,
  ASSET_BASE,
  MODULE_BASE,
  AUDIT_BASE,
  SUBSCRIPTION_BASE,
  ACCESS_MANAGER_BASE,
  SECURITY_BASE,
};

type HttpMethod = "get" | "post" | "put" | "delete" | "patch";

interface RequestOptions {
  signal?: AbortSignal;
  data?: unknown;
  params?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
  responseType?: ResponseType;
  withCredentials?: boolean;
  skipAuth?: boolean;
  /** Unwrap `body.data ?? body` (default true). Set false to receive the raw response body. */
  unwrap?: boolean;
}

export class ApiRequestError extends Error {
  status: number;
  code?: string;
  response?: { status: number; data: unknown };

  constructor(
    message: string,
    status: number,
    code: string | undefined,
    response: { status: number; data: unknown } | undefined,
    cause?: unknown,
  ) {
    super(message, { cause });
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
    this.response = response;
  }
}

export async function request<T>(
  method: HttpMethod,
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    data,
    params,
    headers,
    timeout,
    responseType,
    skipAuth = false,
    unwrap = true,
  } = options;

  try {
    const response = await apiClient.request<T>({
      method,
      url,
      data,
      params,
      headers,
      timeout,
      responseType,
      withCredentials: options.withCredentials,
      skipAuth,
      signal: options.signal,
    });
    const body = response.data;
    return (unwrap ? ((body as { data?: unknown })?.data ?? body) : body) as T;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const info = await handleApiError(error);
      throw new ApiRequestError(
        info.message,
        info.status,
        info.code,
        error.response
          ? { status: error.response.status, data: error.response.data }
          : undefined,
        error,
      );
    }
    throw error;
  }
}

// Legacy positional helper kept for the existing feature services that call
// `api(method, url, data?, params?)`. Prefer `request()` for new code.
export function api<T>(
  method: HttpMethod,
  url: string,
  data?: unknown,
  params?: unknown,
): Promise<T> {
  return request<T>(method, url, { data, params });
}

export default request;
